import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import PayDialog from "../../components/PayDialog";
import { formatPrice } from "../../lib/utils";

const STATUS_VARIANT = { active: "accent", cancelled: "default", expired: "default" };

export default function Memberships() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(null);

  const load = () =>
    api
      .get("/my-memberships")
      .then(({ data }) => setMemberships(data))
      .catch(() => setMemberships([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Memberships</h1>
      {memberships.length === 0 ? (
        <p className="text-muted-foreground">You don't have any memberships yet — look for them on a business profile.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {memberships.map((m) => {
            const expired = new Date(m.current_period_end) < new Date();
            return (
              <Card key={m.id} className="p-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-slate-900">{m.membership?.name}</p>
                  <Badge variant={STATUS_VARIANT[m.status] || "default"}>
                    {m.status === "pending" ? "Awaiting payment" : expired ? "expired" : m.status}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{m.business?.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {m.membership?.services?.map((s) => s.name).join(", ")}
                </p>
                {m.status === "pending" ? (
                  <p className="mt-2 text-sm text-amber-700">
                    Pay at the store — it starts once they confirm your payment.
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-slate-700">
                    {expired ? "Expired" : "Renews"} {new Date(m.current_period_end).toLocaleDateString()}
                  </p>
                )}
                {m.renewal_due && (
                  <p className="mt-1 text-xs text-amber-700">
                    Renewal of {formatPrice(m.renewal_due)} awaiting payment at the business.
                  </p>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-semibold text-slate-900">{formatPrice(m.purchased_price)}</span>
                  {m.status !== "pending" && !m.renewal_due && (
                    <Button size="sm" variant="outline" onClick={() => setRenewing(m)}>
                      Renew
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {renewing && (
        <PayDialog
          title={`Renew ${renewing.membership?.name || "membership"}`}
          amount={Number(renewing.membership?.price ?? renewing.purchased_price)}
          url={`/customer-memberships/${renewing.id}/renew`}
          onClose={() => setRenewing(null)}
          onPaid={() => {
            setRenewing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
